/* The only place in the SPA that calls fetch. Screens go through queries.ts. */

import { clearToken, getToken, isSessionGone, markSessionGone, setToken } from "../auth/tokenStore";
import type { LoginResponse } from "./types";

export const CSRF_COOKIE = "hub_csrf";
export const CSRF_HEADER = "X-CSRF-Token";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
    /** The parsed error body; a 409 carries fields beyond `code` and `message`. */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const AUTH_SIGNED_OUT = "auth:signed-out";

export const setAccessToken = setToken;
export const getAccessToken = getToken;

export function readCookie(name: string): string | null {
  for (const pair of document.cookie.split(";")) {
    const index = pair.indexOf("=");
    if (index < 0) continue;
    if (pair.slice(0, index).trim() === name) return decodeURIComponent(pair.slice(index + 1));
  }
  return null;
}

function baseHeaders(accept: string): Headers {
  const headers = new Headers({ Accept: accept });
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}

async function raise(response: Response): Promise<never> {
  const fallback = `http_${response.status}`;
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    throw new ApiError(response.status, fallback, response.statusText || fallback);
  }
  const parsed = body as Partial<{ code: string; message: string; error: string; detail: unknown }>;
  const code =
    typeof parsed?.code === "string"
      ? parsed.code
      : typeof parsed?.error === "string"
        ? parsed.error
        : fallback;
  const message = typeof parsed?.message === "string" ? parsed.message : code;
  throw new ApiError(response.status, code, message, parsed?.detail, body);
}

async function parseJson<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

function csrfHeaders(accept: string): Headers {
  const headers = baseHeaders(accept);
  const csrf = readCookie(CSRF_COOKIE);
  if (csrf) headers.set(CSRF_HEADER, csrf);
  return headers;
}

/** Refresh bypasses the interceptor, so a 401 here never loops. */
export async function postRefresh(): Promise<LoginResponse> {
  const response = await fetch("/api/auth/refresh", {
    method: "POST",
    credentials: "include",
    headers: csrfHeaders("application/json"),
  });
  if (!response.ok) await raise(response);
  const body = await parseJson<LoginResponse>(response);
  setToken(body.accessToken);
  return body;
}

function publishSignedOut(): void {
  window.dispatchEvent(new CustomEvent(AUTH_SIGNED_OUT));
}

let inFlight: Promise<string | null> | null = null;

/** Concurrent 401s share one refresh; null means the session is gone. */
function refreshSession(): Promise<string | null> {
  if (!inFlight) {
    const pending = postRefresh().then(
      (body) => body.accessToken,
      () => {
        clearToken();
        markSessionGone();
        publishSignedOut();
        return null;
      },
    );
    inFlight = pending;
    void pending.finally(() => {
      if (inFlight === pending) inFlight = null;
    });
  }
  return inFlight;
}

// A 401 from these is an answer, not an expiry.
const NO_REPLAY = new Set([
  "/api/auth/refresh",
  "/api/auth/login",
  "/api/auth/logout",
  "/api/auth/mfa",
  "/api/auth/accept-invite",
]);

async function send(path: string, attempt: () => Promise<Response>): Promise<Response> {
  const first = await attempt();
  if (first.status !== 401 || NO_REPLAY.has(path.split("?")[0] ?? path)) return first;
  if (isSessionGone()) {
    publishSignedOut();
    return first;
  }
  const token = await refreshSession();
  if (token === null) return first;
  const replayed = await attempt();
  if (replayed.status === 401) {
    clearToken();
    publishSignedOut();
  }
  return replayed;
}

export async function apiGet<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await send(path, () => {
    const headers = baseHeaders("application/json");
    new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
    return fetch(path, { ...init, method: "GET", credentials: "include", headers });
  });
  if (!response.ok) await raise(response);
  return parseJson<T>(response);
}

export async function apiSend<T>(
  method: "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const response = await send(path, () => {
    const headers = csrfHeaders("application/json");
    const init: RequestInit = { method, credentials: "include", headers };
    if (signal) init.signal = signal;
    if (body !== undefined) {
      headers.set("Content-Type", "application/json");
      init.body = JSON.stringify(body);
    }
    return fetch(path, init);
  });
  if (!response.ok) await raise(response);
  return parseJson<T>(response);
}

/** Multipart upload. `apiSend` always JSON-encodes its body, so a `File` needs its own path;
    the browser sets the multipart boundary in Content-Type when none is set here. */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const response = await send(path, () => {
    const headers = csrfHeaders("application/json");
    const form = new FormData();
    form.append("file", file);
    return fetch(path, { method: "POST", credentials: "include", headers, body: form });
  });
  if (!response.ok) await raise(response);
  return parseJson<T>(response);
}

export async function apiBlob(path: string): Promise<Blob> {
  const response = await send(path, () =>
    fetch(path, { method: "GET", credentials: "include", headers: baseHeaders("*/*") }),
  );
  if (!response.ok) await raise(response);
  return response.blob();
}

export async function apiText(path: string, accept = "application/xml", init?: RequestInit): Promise<string> {
  const response = await send(path, () => {
    const headers = baseHeaders(accept);
    new Headers(init?.headers).forEach((value, key) => headers.set(key, value));
    return fetch(path, { ...init, method: "GET", credentials: "include", headers });
  });
  if (!response.ok) await raise(response);
  return response.text();
}
